import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, Settings, LogOut } from 'lucide-react';
import styles from './TopBar.module.css';
import CommandPalette from './CommandPalette';
import Avatar from './ui/Avatar';
import ViewAsBar from './ViewAsBar';
import Dropdown from './ui/Dropdown';
import PushToggle from './PushToggle';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { getPageTitle } from '../utils/pageTitles';

const NOTIFICATION_LABELS = {
  task: 'Task',
  announcement: 'Announcement',
  withdrawal: 'Payout',
  sop: 'SOP',
  system: 'System',
};

function timeAgo(timestamp) {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function TopBar({ currentPage }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();

  // The sidebar says where you are, so the bar only has to say *what* you are
  // looking at. A room trail here would repeat the highlighted nav item.
  const pageTitle = useMemo(() => getPageTitle(currentPage, user), [currentPage, user]);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <header className={styles.topbar}>
        <div className={styles.left}>
          <h2 className={styles.pageTitle}>{pageTitle}</h2>
        </div>

        <div className={styles.right}>
          {!user?.viewAs && <ViewAsBar />}
          <button className={styles.searchTrigger} onClick={() => setPaletteOpen(true)} aria-label="Search">
            <Search size={15} />
            <span>Search...</span>
            <kbd>Ctrl K</kbd>
          </button>

          <Dropdown
            align="right"
            trigger={
              <span className={styles.iconBtn} aria-label="Notifications">
                <Bell size={18} />
                {unreadCount > 0 && <span className={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
              </span>
            }
            panelClassName={styles.notificationPanel}
            content={
              <>
                <div className={styles.notificationHeader}>
                  <span>Notifications</span>
                  {unreadCount > 0 && (
                    <button type="button" className={styles.markAllBtn} onClick={markAllRead}>
                      Mark all read
                    </button>
                  )}
                </div>
                {/* The one place to turn notifications on that is on every
                    page. Renders nothing once they're already on. */}
                <PushToggle variant="compact" />

                {notifications.length === 0 ? (
                  <div className={styles.notificationEmpty}>You're all caught up</div>
                ) : (
                  notifications.slice(0, 12).map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      className={[styles.notificationItem, n.read_at ? '' : styles.unread].filter(Boolean).join(' ')}
                      onClick={() => {
                        if (!n.read_at) markRead(n.id);
                        if (n.link) navigate(n.link);
                      }}
                    >
                      <div className={styles.notificationTitle}>
                        {!n.read_at && <span className={styles.unreadDot} aria-hidden="true" />}
                        {n.title}
                      </div>
                      {n.body && <div className={styles.notificationBody}>{n.body}</div>}
                      <div className={styles.notificationSubtitle}>
                        {NOTIFICATION_LABELS[n.type] || 'Update'} · {timeAgo(n.created_at)}
                        {n.link && <span className={styles.notificationCta}>{n.link_label || 'Open'} →</span>}
                      </div>
                    </button>
                  ))
                )}
                <button type="button" className={styles.notificationFooter} onClick={() => navigate('/notifications')}>
                  View all notifications
                </button>
              </>
            }
          />

          <Dropdown
            align="right"
            trigger={
              <span className={styles.userTrigger}>
                <Avatar user={user} size="sm" />
              </span>
            }
            items={[
              // Settings is app configuration, not account preferences — every
              // control on it is manage_users-gated, so for a creator the menu
              // was offering a page that could only answer 403.
              ...(user?.role === 'creator'
                ? [{ label: 'Notifications', icon: Bell, onClick: () => navigate('/notifications') }]
                : [{ label: 'Settings', icon: Settings, onClick: () => navigate('/settings') }]),
              { separator: true },
              { label: 'Log out', icon: LogOut, danger: true, onClick: logout },
            ]}
          />
        </div>
      </header>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}

export default TopBar;
